import { type ReactElement, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  NeonModalDrawer,
  NeonButtonFooter,
  NeonButton,
  NeonCheckboxSingle,
  NeonIcon,
  NeonSearch,
  NeonPagination,
} from '@ps-refarch-ux/neon';
import { NeonButtonTypes } from '@/constants/neon-constants';
import { useGetCourse, useSearchCourses, useUpdateCourse } from '@/repositories/course-repository';
import { useToastNotifications } from '@/hooks/use-toast-notifications';
import type { School } from '@/types/school';
import type { Course } from '@/types';
import {
  SCHOOL_MAPPING_DRAWER_TEXT,
  formatSchoolMappingSuccessToast,
} from '@/constants/course-catalog-constants';
import styles from './course-schools-mapping-drawer.module.scss';

const DRAWER_ID = 'course-schools-mapping-drawer';
const SCHOOL_PAGE_SIZE = 25;

type RadioOption = 'copy' | 'select';

export interface CourseSchoolsMappingDrawerProps {
  courseId: string;
  courseName: string;
  currentSchoolNids: string[];
  allSchools: School[];
  isSchoolsLoading?: boolean;
  defaultRadio?: RadioOption;
  onClose: () => void;
  onSaveSuccess?: () => void;
}

export function CourseSchoolsMappingDrawer({
  courseId,
  courseName,
  currentSchoolNids,
  allSchools,
  isSchoolsLoading = false,
  defaultRadio = 'select',
  onClose,
  onSaveSuccess,
}: CourseSchoolsMappingDrawerProps): ReactElement {
  const { showToast } = useToastNotifications();
  const { data: courseDetail, isLoading: isDetailLoading } = useGetCourse(courseId);
  const updateCourse = useUpdateCourse();
  const { mutate: triggerSearch, data: searchResults, isPending: isSearching } = useSearchCourses();

  // ── radio ──────────────────────────────────────────────────────────────────
  const [radio, setRadio] = useState<RadioOption>(defaultRadio);

  // ── Radio 1: copy from another course ─────────────────────────────────────
  const [copySearchTerm, setCopySearchTerm] = useState('');
  const [selectedCopyCourse, setSelectedCopyCourse] = useState<Course | null>(null);
  const [openSchoolCard, setOpenSchoolCard] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    const term = copySearchTerm.trim();
    if (!term) {
      return;
    }
    debounceRef.current = setTimeout(() => {
      triggerSearch(term);
    }, 300);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [copySearchTerm, triggerSearch]);

  // ── Radio 2: select schools manually ──────────────────────────────────────
  const [selectedSet, setSelectedSet] = useState<Set<string>>(() => new Set(currentSchoolNids));
  const [schoolSearch, setSchoolSearch] = useState('');
  const [schoolPage, setSchoolPage] = useState(1);

  const savedSchoolsKey = `${courseId}:${[...currentSchoolNids].sort().join(',')}`;
  useEffect(() => {
    setSelectedSet(new Set(currentSchoolNids));
    // Intentionally depend on content fingerprint only — parent may pass a new array instance each render
    // with the same school ids; listing currentSchoolNids would reset selection every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- synced when savedSchoolsKey (course + ids) changes
  }, [savedSchoolsKey]);

  const filteredSchools = useMemo(() => {
    if (!schoolSearch.trim()) {
      return allSchools;
    }
    const term = schoolSearch.trim().toLowerCase();
    return allSchools.filter((s) => s.name.toLowerCase().includes(term));
  }, [allSchools, schoolSearch]);

  const pageSchools = useMemo(() => {
    const start = (schoolPage - 1) * SCHOOL_PAGE_SIZE;
    return filteredSchools.slice(start, start + SCHOOL_PAGE_SIZE);
  }, [filteredSchools, schoolPage]);

  const handleToggle = useCallback((nid: string) => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      if (next.has(nid)) {
        next.delete(nid);
      } else {
        next.add(nid);
      }
      return next;
    });
  }, []);

  const handleMapAll = useCallback(() => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      for (const s of allSchools) {
        next.add(s.nid);
      }
      return next;
    });
  }, [allSchools]);

  const handleUnmapAll = useCallback(() => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      for (const s of allSchools) {
        next.delete(s.nid);
      }
      return next;
    });
  }, [allSchools]);

  // ── Save ───────────────────────────────────────────────────────────────────
  // True when the "select" panel's selection differs from the saved school list
  const selectIsDirty = useMemo(() => {
    if (selectedSet.size !== currentSchoolNids.length) {
      return true;
    }
    return currentSchoolNids.some((nid) => !selectedSet.has(nid));
  }, [selectedSet, currentSchoolNids]);

  const isDirty = radio === 'copy' ? selectedCopyCourse !== null : selectIsDirty;

  const isSaveDisabled =
    isDetailLoading ||
    updateCourse.isPending ||
    !isDirty ||
    (radio === 'copy' && selectedCopyCourse === null);

  const handleSave = useCallback(() => {
    if (!courseDetail) {
      return;
    }
    const newSchoolIds =
      radio === 'copy'
        ? Array.from(new Set(selectedCopyCourse?.schools ?? []))
        : Array.from(selectedSet);
    updateCourse.mutate(
      { course: { ...courseDetail, schools: newSchoolIds } },
      {
        onSuccess: () => {
          showToast('success', formatSchoolMappingSuccessToast(courseName, newSchoolIds.length));
          onSaveSuccess?.();
          onClose();
        },
        onError: () => {
          showToast('error', {
            heading: `Unable to update school mapping for ${courseName}. Please try again.`,
          });
        },
      }
    );
  }, [
    courseDetail,
    radio,
    selectedCopyCourse,
    selectedSet,
    updateCourse,
    showToast,
    courseName,
    onSaveSuccess,
    onClose,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Exclude the course being mapped from copy results, to prevent self-copy
  const courseSearchResults = useMemo(
    () => (searchResults ?? []).filter((c) => c.id !== courseId),
    [searchResults, courseId]
  );

  return (
    <NeonModalDrawer
      id={DRAWER_ID}
      neonDrawerHasClosed={onClose}
      dataSize="medium"
      dataShowCloseButton="true"
      dataShowHeaderDivider="true"
      dataShowFooterDivider="true"
      dataCloseOnBackdropClick="true"
    >
      <div data-slot="drawer-header-title">Map course to schools</div>

      <div data-slot="drawer-body">
        <p className={styles.drawerCourseTitle}>{courseName}</p>
        <p className={styles.drawerIntroText}>{SCHOOL_MAPPING_DRAWER_TEXT.DrawerIntroText}</p>

        {/* ── Radio options with inline panels ── */}
        <div className={styles.radioGroup}>
          {/* Radio 1: copy from existing course */}
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="course-mapping-radio"
              value="copy"
              checked={radio === 'copy'}
              onChange={() => {
                setRadio('copy');
                setCopySearchTerm('');
                setSelectedCopyCourse(null);
                setOpenSchoolCard(false);
                setSelectedSet(new Set(currentSchoolNids));
              }}
            />
            <span>{SCHOOL_MAPPING_DRAWER_TEXT.RadioCopyFromCourse}</span>
          </label>

          {/* Copy panel — shown inline under radio 1 when selected */}
          {radio === 'copy' && (
            <div className={styles.radioIndentedPanel}>
              <NeonSearch
                id="copy-course-search"
                dataPlaceholderText="Search by course name or ID"
                neonSearchValueChange={(value: string) => {
                  setCopySearchTerm(value);
                }}
              />

              {!copySearchTerm && selectedCopyCourse !== null && (
                <div className={styles.copySelectedCard}>
                  <div className={styles.copySelectedHeader}>
                    <div className={styles.copyCardCourseMeta}>
                      <span className={styles.copyCardCourseName}>{selectedCopyCourse.name}</span>
                      <span className={styles.copyCardCourseId}>{selectedCopyCourse.id}</span>
                    </div>
                    <button
                      type="button"
                      title="Delete"
                      aria-label={`Delete ${selectedCopyCourse.name}`}
                      onClick={() => {
                        setSelectedCopyCourse(null);
                        setOpenSchoolCard(false);
                      }}
                      className={styles.iconButton}
                    >
                      <NeonIcon dataIcon="delete" />
                    </button>
                  </div>

                  {/* Schools section */}
                  <div className={styles.copyCardSchoolsSection}>
                    {(selectedCopyCourse.schools?.length ?? 0) > 0 ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setOpenSchoolCard((prev) => !prev)}
                          aria-expanded={openSchoolCard}
                          className={styles.copyCardSchoolsToggleRow}
                        >
                          <span className={styles.copyCardSchoolsLabel}>
                            {SCHOOL_MAPPING_DRAWER_TEXT.SchoolsMappedLabel}
                          </span>
                          <NeonIcon
                            dataIcon="caret-down"
                            aria-hidden="true"
                            className={`${styles.copyCardChevronIcon}${openSchoolCard ? ` ${styles.copyCardChevronOpen}` : ''}`}
                          />
                        </button>
                        <span className={styles.copyCardSchoolsCount}>
                          {`${selectedCopyCourse.schools!.length} school${selectedCopyCourse.schools!.length !== 1 ? 's' : ''}`}
                        </span>
                      </>
                    ) : (
                      <span className={styles.copyCardSchoolsCount}>
                        {SCHOOL_MAPPING_DRAWER_TEXT.NoSchoolsMapped}
                      </span>
                    )}
                  </div>

                  {openSchoolCard && (selectedCopyCourse.schools?.length ?? 0) > 0 && (
                    <div className={styles.copyPreviewList}>
                      {(selectedCopyCourse.schools ?? []).map((nid) => {
                        const school = allSchools.find((s) => s.nid === nid);
                        return (
                          <div key={nid} className={styles.copyPreviewSchoolName}>
                            {school?.name ?? nid}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {copySearchTerm.trim().length > 0 && (
                <div className={styles.searchResultsBox}>
                  {isSearching && <p className={styles.paddedMuted}>Searching…</p>}
                  {!isSearching && courseSearchResults.length === 0 && (
                    <p className={styles.paddedMuted}>No courses found</p>
                  )}
                  {!isSearching &&
                    courseSearchResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setSelectedCopyCourse(c);
                          setOpenSchoolCard(false);
                          setCopySearchTerm('');
                        }}
                        className={styles.searchResultButton}
                      >
                        <span className={styles.searchResultName}>{c.name}</span>
                        <span className={styles.searchResultId}>{c.id}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}

          {/* Radio 2: select schools manually */}
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="course-mapping-radio"
              value="select"
              checked={radio === 'select'}
              onChange={() => {
                setRadio('select');
                setCopySearchTerm('');
                setSelectedCopyCourse(null);
                setOpenSchoolCard(false);
                setSelectedSet(new Set(currentSchoolNids));
                setSchoolPage(1);
              }}
            />
            <span>{SCHOOL_MAPPING_DRAWER_TEXT.RadioSelectSchools}</span>
          </label>

          {/* Select panel — shown inline under radio 2 when selected */}
          {radio === 'select' && (
            <div className={styles.radioIndentedPanel}>
              <div className={styles.manualToolbar}>
                <NeonButton
                  id="course-mapping-map-all"
                  dataType={NeonButtonTypes.Secondary}
                  dataText="Map all"
                  disabled={isSchoolsLoading}
                  onClick={handleMapAll}
                />
                <NeonButton
                  id="course-mapping-unmap-all"
                  dataType={NeonButtonTypes.Secondary}
                  dataText="Unmap all"
                  disabled={isSchoolsLoading}
                  onClick={handleUnmapAll}
                />
              </div>

              <NeonSearch
                id="school-name-search"
                dataPlaceholderText="Search school name"
                disabled={isSchoolsLoading}
                neonSearchValueChange={(value: string) => {
                  setSchoolSearch(value);
                  setSchoolPage(1);
                }}
              />

              <div className={styles.columnHeader}>School</div>

              <div>
                {isSchoolsLoading && (
                  <p className={styles.centerMuted} data-testid="schools-loading">
                    Loading schools…
                  </p>
                )}
                {!isSchoolsLoading && pageSchools.length === 0 && (
                  <p className={styles.centerMuted}>No schools found</p>
                )}
                {!isSchoolsLoading &&
                  pageSchools.map((school) => (
                    <div
                      key={school.nid}
                      className={styles.schoolRow}
                      data-testid={`school-row-${school.nid}`}
                    >
                      <NeonCheckboxSingle
                        id={`school-toggle-${school.nid}`}
                        dataCheckType="toggle"
                        dataLabelText={school.name}
                        value={selectedSet.has(school.nid)}
                        onInput={() => handleToggle(school.nid)}
                      />
                    </div>
                  ))}
              </div>

              {!isSchoolsLoading && filteredSchools.length > SCHOOL_PAGE_SIZE && (
                <NeonPagination
                  id="course-mapping-schools-pagination"
                  dataArrayLength={filteredSchools.length}
                  dataCurrentPage={schoolPage}
                  dataPageLength={SCHOOL_PAGE_SIZE}
                  dataShowJumpTo="true"
                  neonPaginationChange={(newPage: number) => setSchoolPage(newPage)}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <div data-slot="drawer-button-footer">
        <NeonButtonFooter
          id="course-schools-mapping-footer"
          buttons={[
            {
              id: 'course-schools-mapping-cancel',
              text: 'Cancel',
              type: NeonButtonTypes.Borderless,
              onClick: handleCancel,
            },
          ]}
          primaryButton={{
            id: 'course-schools-mapping-save',
            text: SCHOOL_MAPPING_DRAWER_TEXT.SaveMapping,
            type: NeonButtonTypes.Primary,
            disabled: isSaveDisabled,
            onClick: handleSave,
          }}
        />
      </div>
    </NeonModalDrawer>
  );
}
